import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CheckboxFormFieldComponent } from "../fields/checkbox-form-field/checkbox-form-field.component";
import { TranslateModule } from '@ngx-translate/core';
import { NotificationSettings } from 'app/profile/profile.model';

@Component({
  selector: 'app-notifications-form',
  standalone: true,
  imports: [CheckboxFormFieldComponent, TranslateModule, ReactiveFormsModule],
  templateUrl: './notifications-form.component.html',
  styleUrl: './notifications-form.component.scss'
})
export class NotificationsFormComponent {
  @Input() notificationSettings!: NotificationSettings;
  notificationsForm!: FormGroup;
  initialValues: NotificationSettings | null = null;

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initForm();
    this.setInitialValues();
    this.setupValueChangeSubscriptions();
  }

  initForm(): void {
    this.notificationsForm = this.fb.group({
      selectAll: [false],
      engageApps: [this.notificationSettings?.engageApps ?? false],
      requestToEngage: [this.notificationSettings?.requestToEngage ?? false],
      ownerChanged: [this.notificationSettings?.ownerChanged ?? false],
      ticketStatusChange: [this.notificationSettings?.ticketStatusChange ?? false],
      mentionsInComment: [this.notificationSettings?.mentionsInComment ?? false],
      pendingTicketTimeTracking: [this.notificationSettings?.pendingTicketTimeTracking ?? false]
    });
  }

  setInitialValues(): void {
    this.initialValues = { ...this.notificationsForm.value };
  }

  setupValueChangeSubscriptions(): void {
    this.notificationsForm.valueChanges.subscribe(() => {
      this.updateSelectAllState();
      this.updateFormPristineState();
    });

    this.getFormControl('selectAll')?.valueChanges.subscribe((isSelectedAll) => {
      this.toggleAllCheckboxes(isSelectedAll);
    });
  }

  toggleAllCheckboxes(isSelected: boolean): void {
    Object.keys(this.notificationsForm.controls).forEach(key => {
      if (key !== 'selectAll') {
        this.notificationsForm.get(key)?.setValue(isSelected, { emitEvent: false });
      }
    });
  }

  updateSelectAllState(): void {
    const allChecked = this.areAllCheckboxesChecked();
    this.getFormControl('selectAll')?.setValue(allChecked, { emitEvent: false });
  }

  areAllCheckboxesChecked(): boolean {
    return Object.keys(this.notificationsForm.controls)
      .filter(key => key !== 'selectAll')
      .every(key => this.notificationsForm.get(key)?.value === true);
  }

  updateFormPristineState(): void {
    const currentValues = this.notificationsForm.value;
    const isPristine = JSON.stringify(this.initialValues) === JSON.stringify(currentValues);
    if (isPristine) {
      this.notificationsForm.markAsPristine();
    } else {
      this.notificationsForm.markAsDirty();
    }
  }

  getFormControl(controlName: string): FormControl {
    return this.notificationsForm.get(controlName) as FormControl;
  }

  checkFormState(): void {
    this.updateSelectAllState();
    this.updateFormPristineState();
  }

  resetForm(): void {
    this.notificationsForm.reset({
      ...this.initialValues,
      selectAll: this.areAllCheckboxesChecked()
    });
    this.notificationsForm.markAsPristine();
  }

  getNotificationSettings(): NotificationSettings {
    return this.notificationsForm.value as NotificationSettings ?? undefined;
  }
}
